import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useContractsQuery } from "../api/queries";
import { ContractsFilter } from "../ui/ContractsFilter";
import { ContractsTable } from "../ui/ContractsTable";
import { ContractsToolbar } from "../ui/ContractsToolbar";
import { ContractsTour, useContractsTour } from "../tour/ContractsTour";

export function ContractsPage() {
  const tour = useContractsTour();
  const nav = useNavigate({ from: "/app/contracts" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/contracts" });

  const list = useContractsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["contracts:create"]);

  return (
    <>
      <Header />

      <Main>
        <ContractsTour run={tour.run} onCallback={tour.handleJoyrideCallback} />
        <ContractsToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/contracts/new" })}
          onStartTour={tour.startTour}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ContractsFilter />

          <ContractsTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(d) => nav({ to: "/app/contracts/$id/edit", params: { id: d.id } })}
            onView={(d) => nav({ to: "/app/contracts/$id", params: { id: d.id } })}
          />
        </div>
      </Main>
    </>
  );
}
